import classNames from 'classnames';

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { Proposal } from '../../Proposal';
import { ProposalDataElement } from '../../ProposalElements';

import { AltMetricsDetails, DataPoints } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { CurrencyCell, PercentageCell, TextCell } from '@/views/components/cells';
import { ComparisonItemDAO } from '../ProposalItemDAO';

export default class AltsMetricsDAO extends ComparisonItemBasedProposalDAO {
  metricsFieldClasses = classNames(ProposalStyleClassNames.ProposalTableContent, ProposalStyleClassNames.ProposalTableIndentField);

  i18nBasePath = 'Proposal.Modules.AltMetrics';

  public static buildInstance(proposal: Proposal, proposalElement: ProposalDataElement): ProposalDAOInterface {
    return new AltsMetricsDAO(proposal, proposalElement);
  }

  protected getColorKeys(): string[] {
    return ['ALTS_METRICS_COLORS'];
  }

  protected build(): void {
    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      const altMetricsData = DataPoints.getAltMetrics(itemDAO.item);
      const renderConfig = this.itemToMetricsConfig(altMetricsData.response);

      if (altMetricsData.disclaimerKeys) {
        this.addDisclaimerKeys(altMetricsData.disclaimerKeys, itemDAO.item);
      }

      for (const category of renderConfig) {
        this.addFieldDAO(itemDAO, new ProposalFieldDAO('', ''));
        this.addFieldName(category.title, classNames(ProposalStyleClassNames.ProposalTableGroup, ProposalStyleClassNames.ProposalTableContent));

        for (const field of category.fields) {
          this.addMetricField(itemDAO, field.label, field.value, field.cellType);
        }
      }
    }
  }

  addMetricField(itemDAO: ComparisonItemDAO, fieldName: string, fieldValue: any, cellType: any): void {
    const field = new ProposalFieldDAO(fieldName, fieldValue, this.metricsFieldClasses);
    field.cellType = cellType;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(fieldName, this.metricsFieldClasses);
  }

  itemToMetricsConfig = (altMetricsData: AltMetricsDetails) => {
    const {
      manufacturer,
      vintage,
      status,
      commitment,
      called,
      distributed,
      remaining,
      managementFee,
      chargeFrequency,
      hurdleRate,
      carriedInterest,
      gpCatchUpRate,
      fundFormationCost,
      netIrr,
      medianBenchmarkNetIrr,
      quartile,
    } = altMetricsData;

    return [
      {
        title: this.formatMessage(`${this.i18nBasePath}.Overview.Title`),
        fields: [
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.FirmName`),
            cellType: TextCell,
            value: manufacturer,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.Vintage`),
            cellType: TextCell,
            value: vintage,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.Status`),
            cellType: TextCell,
            value: status,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.Commitment`),
            cellType: CurrencyCell,
            value: { value: commitment?.size },
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.GPCommitmentsToFund`),
            cellType: CurrencyCell,
            value: { value: commitment?.gpCommitmentValue },
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.Called`),
            cellType: CurrencyCell,
            value: { value: called },
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.Distributed`),
            cellType: CurrencyCell,
            value: { value: distributed },
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Overview.Remaining`),
            cellType: CurrencyCell,
            value: { value: remaining },
          },
        ],
      },
      {
        title: this.formatMessage(`${this.i18nBasePath}.FeeInformation.Title`),
        fields: [
          {
            label: this.formatMessage(`${this.i18nBasePath}.FeeInformation.DuringInvestmentPeriod`),
            cellType: PercentageCell,
            value: managementFee?.investPeriod,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.FeeInformation.PostInvestmentPeriod`),
            cellType: PercentageCell,
            value: managementFee?.postInvestPeriod,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.FeeInformation.ChargeFrequency`),
            cellType: TextCell,
            value: chargeFrequency,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.FeeInformation.HurdleRate`),
            cellType: PercentageCell,
            value: hurdleRate,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.FeeInformation.CarriedInterest`),
            cellType: PercentageCell,
            value: carriedInterest?.rate,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.FeeInformation.GPCatchUpRate`),
            cellType: PercentageCell,
            value: gpCatchUpRate,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.FeeInformation.FundFormationCost`),
            cellType: CurrencyCell,
            value: { value: fundFormationCost },
          },
        ],
      },
      {
        title: this.formatMessage(`${this.i18nBasePath}.Performance.Title`),
        fields: [
          {
            label: this.formatMessage(`${this.i18nBasePath}.Performance.NetIRR`),
            cellType: PercentageCell,
            value: netIrr,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Performance.MedianBenchmarkIRR`),
            cellType: PercentageCell,
            value: medianBenchmarkNetIrr,
          },
          {
            label: this.formatMessage(`${this.i18nBasePath}.Performance.Quartile`),
            cellType: TextCell,
            value: quartile,
          },
        ],
      },
    ];
  };
}
